// Shared UI components — reusable across the platform
import { type ReactNode } from "react";
import { BadgeCheck, AlertCircle, Loader2 } from "lucide-react";
import { PPRAStatus } from "@/types";

// ─── Loading Spinner ──────────────────────────────────────────────────────────

export function LoadingSpinner({ className = "", size }: { className?: string; size?: "sm" | "md" | "lg" }) {
  const sizeClass = size === "sm" ? "h-4 w-4" : size === "lg" ? "h-8 w-8" : "h-5 w-5";
  return <Loader2 className={`animate-spin ${sizeClass} text-sun ${className}`} />;
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

interface StatusBadgeProps {
  status: string;
  variant?: "default" | "success" | "warning" | "error" | "info";
}

const variantStyles = {
  default: "badge-default",
  success: "badge-success",
  warning: "badge-warning",
  error: "badge-error",
  info: "badge-info",
};

export function StatusBadge({ status, variant = "default" }: StatusBadgeProps) {
  return (
    <span className={variantStyles[variant]}>
      {status}
    </span>
  );
}

// ─── Trust Score Badge ────────────────────────────────────────────────────────

export function TrustScoreBadge({ score }: { score: number }) {
  const color = score >= 80 ? "text-mint" : score >= 60 ? "text-yellow-400" : "text-red-400";
  return (
    <div className="flex items-center gap-1">
      <BadgeCheck className={`h-4 w-4 ${color}`} />
      <span className={`text-sm font-semibold ${color}`}>{score.toFixed(0)}</span>
    </div>
  );
}

// ─── PPRA Verification Badge ──────────────────────────────────────────────────

export function PPRABadge({ status }: { status: PPRAStatus }) {
  switch (status) {
    case PPRAStatus.Verified:
      return <StatusBadge status="PPRA Verified" variant="success" />;
    case PPRAStatus.Submitted:
      return <StatusBadge status="Awaiting Review" variant="warning" />;
    case PPRAStatus.Rejected:
      return <StatusBadge status="Verification Rejected" variant="error" />;
  }
}

// ─── Card Component ───────────────────────────────────────────────────────────

interface CardProps {
  children: ReactNode;
  className?: string;
  padding?: boolean;
  onClick?: () => void;
}

export function Card({ children, className = "", padding = true, onClick }: CardProps) {
  return (
    <div onClick={onClick} className={`${onClick ? "card-hover" : "card"} ${!padding ? "p-0" : ""} ${className}`}>
      {children}
    </div>
  );
}

// ─── Page Header ──────────────────────────────────────────────────────────────

interface PageHeaderProps {
  title: string;
  description?: string;
  subtitle?: string;
  actions?: ReactNode;
}

export function PageHeader({ title, description, subtitle, actions }: PageHeaderProps) {
  return (
    <div className="page-header">
      <div>
        <h1 className="heading-page">{title}</h1>
        {(description || subtitle) && <p className="mt-1 text-muted">{description || subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-3">{actions}</div>}
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

interface EmptyStateProps {
  icon?: React.ElementType;
  title: string;
  description?: string;
  action?: ReactNode;
}

export function EmptyState({ icon: Icon, title, description, action }: EmptyStateProps) {
  return (
    <div className="empty-state">
      {Icon && <Icon className="mx-auto h-12 w-12 text-white/20" />}
      <h3 className="mt-4 text-lg font-medium text-white/80">{title}</h3>
      {description && <p className="mt-2 text-muted">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

// ─── Alert ────────────────────────────────────────────────────────────────────

interface AlertProps {
  type: "info" | "warning" | "error" | "success";
  children: ReactNode;
  className?: string;
}

export function Alert({ type, children, className = "" }: AlertProps) {
  const styles = {
    info: "alert-info",
    warning: "alert-warning",
    error: "alert-error",
    success: "alert-success",
  };

  return (
    <div className={`${styles[type]} ${className}`}>
      <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}

// ─── Button ───────────────────────────────────────────────────────────────────

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
}

export function Button({ variant = "primary", size = "md", loading, children, className = "", disabled, ...props }: ButtonProps) {
  const variants = {
    primary: "btn-primary",
    secondary: "btn-secondary",
    danger: "btn-danger",
    ghost: "btn-ghost",
  };
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "",
    lg: "px-6 py-3 text-base",
  };

  return (
    <button
      className={`${variants[variant]} ${sizes[size]} ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="animate-spin h-4 w-4 mr-2" />}
      {children}
    </button>
  );
}
